import type { CSSProperties } from "react";
import { STATUS_PALETTE } from "@/lib/tokens";
import { t } from "@/lib/strings";

/** `unfinalized` = a draft whose meeting date has PASSED (the SeriesPage list marks it
 *  amber: the record of a held meeting is still open). */
export type MemoBadgeStatus = "draft" | "finalized" | "unfinalized";

interface StatusBadgeProps {
  status: MemoBadgeStatus;
}

const BADGES: Record<MemoBadgeStatus, { tint: (typeof STATUS_PALETTE)["blue"]; label: string }> = {
  draft: { tint: STATUS_PALETTE.blue, label: t.memoStatusDraft },
  finalized: { tint: STATUS_PALETTE.green, label: t.memoStatusFinalized },
  unfinalized: { tint: STATUS_PALETTE.amber, label: t.memoStatusUnfinalized },
};

/** Draft/Finalisiert pill for memo headers and list rows. Reuses the same
 * `.flow-status-tint` mechanism as `StatusPill` (blue/green/amber from
 * `STATUS_PALETTE`) so dark-mode legibility (translucent chip + brightened
 * text derived from the dot hue) is already handled — see index.css. */
export function StatusBadge({ status }: StatusBadgeProps) {
  const { tint, label } = BADGES[status];

  return (
    <span
      className="flow-status-tint"
      data-testid={`status-badge-${status}`}
      style={
        {
          display: "inline-flex",
          alignItems: "center",
          borderRadius: 999,
          padding: "3px 10px",
          fontSize: 11.5,
          fontWeight: 600,
          lineHeight: 1.3,
          whiteSpace: "nowrap",
          "--pill-bg": tint.bg,
          "--pill-fg": tint.fg,
          "--pill-dot": tint.dot,
        } as CSSProperties
      }
    >
      {label}
    </span>
  );
}
